import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';

export function IncidentDrawerDetailStep2Snapshot({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as Record<string, any>;
  const snapshotStatus = String((s.selectedIncident as any)?.snapshot_status || '').toUpperCase();
  const hasSnapshot =
    Boolean(s._bestSnapshotAttachment(s.selectedIncident)?.filename) ||
    Boolean((s.selectedIncident as any)?.snapshot_uri) ||
    ['AVAILABLE', 'PENDING', 'FAILED'].includes(snapshotStatus) ||
    Boolean(s.latestSnapshotLoading) ||
    Boolean(s.latestSnapshotError) ||
    Boolean(s.latestSnapshotObjectUrl);
  const primaryImageLabel = String(s.primaryReviewerImageLabel || '').trim() || 'Raw source evidence';
  const bboxStatusLabel = String(s.reviewerAidBboxStatusLabel || '').trim();
  const secondaryEvidenceLabel = String(s.secondaryEvidenceLabel || '').trim();
  const incidentSubjectMetadataChipLabel = String(s.incidentSubjectMetadataChipLabel || '').trim();
  const forkliftSubjectMetadataChipLabel = String(s.forkliftSubjectMetadataChipLabel || '').trim();
  const snapshotConfidenceLabel = String(s.snapshotConfidenceLabel || '').trim();
  const forkliftConfidenceLabel = String(s.forkliftConfidenceLabel || '').trim();
  const triggerVerificationLabel = String(s.triggerVerificationLabel || '').trim();
  const triggerVerificationStatus = String(s.triggerVerificationStatus || '').trim().toLowerCase();
  const secondarySnapshotFilename = String(s.sourceTruthSnapshotRef?.filename || '').trim();
  const secondaryClipFilename = String(s.sourceTruthClipRef?.filename || '').trim();
  const secondaryEvidenceFilename = secondarySnapshotFilename || secondaryClipFilename;
  const secondaryEvidenceType = secondarySnapshotFilename ? 'snapshot' : secondaryClipFilename ? 'clip' : '';
  const showPrimaryUnavailableAlert =
    Boolean(s.strictReviewerImagePolicy) &&
    !s.latestSnapshotLoading &&
    !s.latestSnapshotObjectUrl &&
    !s.latestSnapshotError &&
    Boolean(secondaryEvidenceFilename);

  const handleDownloadSecondaryEvidence = async () => {
    if (!s.selectedIncident?.incident_id || !secondaryEvidenceFilename || !s.fetchEvidenceFileBlob || !s.downloadBlob) return;
    try {
      const blob = await s.fetchEvidenceFileBlob(s.selectedIncident.incident_id, secondaryEvidenceFilename);
      s.downloadBlob(blob, secondaryEvidenceFilename);
    } catch {
      // The primary snapshot panel already surfaces snapshot fetch failures.
    }
  };

  if (!hasSnapshot) {
    return (
      <Alert severity="info" sx={{ mb: 1.5 }}>
        {s.groupedReviewMode ? 'No representative evidence attached yet.' : 'No snapshot evidence attached yet.'}
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={s.drawerCardSx}>
      <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step2')} phase</Typography>
      <Typography sx={{ ...s.cardHeadingSx, mb: 1.1 }}>Snapshot frame</Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        <Chip size="small" color="info" variant="outlined" label={primaryImageLabel} />
        {secondaryEvidenceLabel && secondaryEvidenceFilename ? (
          <Chip size="small" variant="outlined" label={secondaryEvidenceLabel} />
        ) : null}
        {incidentSubjectMetadataChipLabel ? (
          <Chip size="small" variant="outlined" label={incidentSubjectMetadataChipLabel} />
        ) : null}
        {snapshotConfidenceLabel ? (
          <Chip size="small" variant="outlined" label={snapshotConfidenceLabel} />
        ) : null}
        {forkliftSubjectMetadataChipLabel ? (
          <Chip size="small" variant="outlined" label={forkliftSubjectMetadataChipLabel} />
        ) : null}
        {forkliftConfidenceLabel ? (
          <Chip size="small" variant="outlined" label={forkliftConfidenceLabel} />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {s._bestSnapshotAttachment(s.selectedIncident)?.filename || 'Latest evidence snapshot'}
      </Typography>
      {s.snapshotLooksDelayed ? (
        <Alert severity="warning" sx={{ mb: 1.25 }}>
          This snapshot is {s.formatTimingDelta(s.snapshotDeltaMs)} from the evidence target. Treat it as scene context
          unless the observed condition is still visible in-frame.
        </Alert>
      ) : null}
      {bboxStatusLabel ? (
        <Alert severity="info" sx={{ mb: 1.25 }}>
          {bboxStatusLabel}
        </Alert>
      ) : null}
      {triggerVerificationLabel ? (
        <Alert severity={triggerVerificationStatus === 'verified' ? 'success' : 'info'} sx={{ mb: 1.25 }}>
          {triggerVerificationLabel}
        </Alert>
      ) : null}
      {s.latestSnapshotError ? (
        <Alert severity="warning">{s.latestSnapshotError}</Alert>
      ) : s.latestSnapshotLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : s.latestSnapshotObjectUrl ? (
        <Box
          component="img"
          alt="Evidence snapshot"
          src={s.latestSnapshotObjectUrl}
          sx={{
            width: '100%',
            maxHeight: 420,
            objectFit: 'contain',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.default',
          }}
        />
      ) : (
        <Alert severity="info">Snapshot evidence is present but not loaded yet.</Alert>
      )}
      {showPrimaryUnavailableAlert ? (
        <Alert severity="info" sx={{ mt: 1.25 }}>
          Reviewer aid image is unavailable for this incident. The retained raw source evidence is listed below instead
          of being swapped into the main reviewer image slot.
        </Alert>
      ) : null}
      {s.snapshotFrameTimeMs ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Snapshot frame time {s.formatTimingInstant(s.snapshotFrameTimeMs)}
          {s.snapshotDeltaMs != null ? ` (Δ ${s.formatTimingDelta(s.snapshotDeltaMs)} from evidence target)` : ''}.
        </Typography>
      ) : null}
      {s.snapshotCapturedAtMs ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Evidence file written on edge at {s.formatTimingInstant(s.snapshotCapturedAtMs)}.
        </Typography>
      ) : null}
      {!s.snapshotCapturedAtMs && s.snapshotCloudReceivedAtMs ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Cloud received this evidence file at {s.formatTimingInstant(s.snapshotCloudReceivedAtMs)}.
        </Typography>
      ) : null}
      {secondaryEvidenceLabel && secondaryEvidenceFilename ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.4 }}>
            {secondaryEvidenceLabel}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.8 }}>
            {secondaryEvidenceFilename}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => void handleDownloadSecondaryEvidence()}>
            {secondaryEvidenceType === 'clip' ? 'Download raw clip' : 'Download raw frame'}
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
}
